import type { CSSProperties, ReactNode } from 'react';

/** The Hive hexagon mark (outline hexagon with a solid inner hexagon). */
export function HiveMark({ size = 28, tone = 'accent' }: { size?: number; tone?: 'accent' | 'white' }) {
  const colour = tone === 'white' ? 'var(--bm-white)' : 'var(--accent)';
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={colour} strokeWidth="1.5" strokeLinejoin="round">
      <path d="M12 2.5l8.2 4.75v9.5L12 21.5l-8.2-4.75v-9.5z" />
      <path d="M12 8l3.5 2v4L12 16l-3.5-2v-4z" fill={colour} stroke="none" />
    </svg>
  );
}

/** "Hive" plus the product name (Keeper, Master control). */
export function Wordmark({ product, tone = 'accent', size = 28 }: { product?: string; tone?: 'accent' | 'white'; size?: number }) {
  return (
    <span className="hv-wordmark">
      <HiveMark size={size} tone={tone} />
      <span className="hv-wordmark__name">Hive</span>
      {product && <span className="hv-wordmark__product">{product}</span>}
    </span>
  );
}

/** The BeeMedia seven-dot mark. */
export function BeeMediaMark({ size = 18 }: { size?: number }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24">
      <rect width="24" height="24" rx="6" fill="var(--accent)" />
      <g fill="var(--bm-white)">
        <circle cx="12" cy="12" r="1.8" />
        <circle cx="12" cy="6.4" r="1.8" />
        <circle cx="16.85" cy="9.2" r="1.8" />
        <circle cx="16.85" cy="14.8" r="1.8" />
        <circle cx="12" cy="17.6" r="1.8" />
        <circle cx="7.15" cy="14.8" r="1.8" />
        <circle cx="7.15" cy="9.2" r="1.8" />
      </g>
    </svg>
  );
}

export function PoweredBy({ suffix }: { suffix?: string }) {
  return (
    <span className="hv-powered">
      <BeeMediaMark />
      <span>Powered by BeeMedia{suffix ? ` · ${suffix}` : ''}</span>
    </span>
  );
}

export type HexTone = 'deep' | 'light' | 'teal' | 'blue' | 'amber' | 'red' | 'muted';

/** Venue or screen monogram in a hexagon. */
export function HexMark({
  label,
  size = 44,
  tone = 'deep',
  children,
  title,
}: {
  label?: string;
  size?: number;
  tone?: HexTone;
  children?: ReactNode;
  title?: string;
}) {
  const style: CSSProperties = { width: size, height: size, fontSize: Math.round(size * 0.34) };
  return (
    <span className={`hv-hex hv-hex--${tone}`} style={style} aria-hidden={title ? undefined : true} title={title}>
      {children ?? label}
    </span>
  );
}

/** Two-letter monogram from a venue name: "Irish Village" → "IV". */
export function monogram(name: string): string {
  const words = name.replace(/[^\p{L}\p{N} ]/gu, '').split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? words[0]?.[1] ?? '')).toUpperCase();
}

/**
 * The faint hexagon lattice used behind brand surfaces, fading from one corner.
 * `ink` is a CSS colour expression (a token var).
 */
export function HexPattern({ ink = 'var(--bm-blue-deep)', opacity = 0.12, corner = 'top-right', id = 'hv-hx' }: {
  ink?: string;
  opacity?: number;
  corner?: 'top-right' | 'bottom-left';
  id?: string;
}) {
  const [cx, cy] = corner === 'top-right' ? ['100%', '0%'] : ['0%', '100%'];
  return (
    <svg aria-hidden="true" className="hv-pattern" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id={`${id}-p`} width="96" height="55.43" patternUnits="userSpaceOnUse">
          <path d="M64 27.71L48 55.43L16 55.43L0 27.71L16 0L48 0ZM64 27.71L96 27.71" fill="none" stroke={ink} strokeWidth="0.8" />
        </pattern>
        <radialGradient id={`${id}-g`} cx={cx} cy={cy} r="75%">
          <stop offset="0" stopColor="var(--bm-white)" stopOpacity="1" />
          <stop offset="1" stopColor="var(--bm-white)" stopOpacity="0" />
        </radialGradient>
        <mask id={`${id}-m`}>
          <rect width="100%" height="100%" fill={`url(#${id}-g)`} />
        </mask>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id}-p)`} mask={`url(#${id}-m)`} opacity={opacity} />
    </svg>
  );
}
